import React from "react";
import { useParams, useNavigate } from "react-router";
import useInvestorAmlDetailStore from "../zstore/investorAmlDetail";
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineSeparator,
  timelineItemClasses,
} from "@mui/lab";
import { TimeLineItem } from "../api/transformers/aml";
import {
  Stack,
  Typography,
  Button,
  Box,
  CircularProgress,
  Grid,
} from "@mui/material";
import Header from "components/Header";
import PageWrapper from "components/PageWrapper";
import { StatusColor, StatusNaming } from "../constant/amlConstant";
import AmlActions from "../components/AmlActions";
import useTimeLineStore from "../zstore/timeLineStore";
import moment from "moment";

const TimeLinePage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    fetchData: fetchInvestorAmlDetail,
    data: investorAmlDetail,
    loading: investorAmlDetailLoading,
  } = useInvestorAmlDetailStore();

  const {
    fetchData: fetchTimeLine,
    data: timeLineData,
    loading: timeLineLoading,
  } = useTimeLineStore();

  React.useEffect(() => {
    if (id) {
      fetchInvestorAmlDetail(id);
      fetchTimeLine(id);
    }
  }, []);

  if (investorAmlDetailLoading || timeLineLoading) {
    return (
      <Box
        sx={{
          minHeight: 400,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const getTimelineItemTitle = (item: TimeLineItem) => {
    if (item.sourceId) {
      return `${StatusNaming[item.statusApplied]} by ${item.sourceId}`;
    }
    return StatusNaming[item.statusApplied];
  };

  const renderTimelineContent = (item: TimeLineItem) => {
    const momentDatetime = formatDateTime(item?.date, item?.time);
    return (
      <Stack
        direction="column"
        rowGap="4px"
        sx={{
          border: (theme) =>
            theme.palette.mode === "light"
              ? "1px solid #f0f0f0"
              : "1px solid #2a2a2a",
          borderRadius: "8px",
          padding: "12px",
          backgroundColor: (theme) =>
            theme.palette.mode === "light" ? "#fff" : "#121212",
          width: "100%",
        }}
      >
        <Grid container>
          <Grid item xs={12} sm={2} md={2} textAlign={"center"}>
            <Typography sx={{ fontWeight: 500 }}>
              {momentDatetime.format("DD/MM/YYYY")}
            </Typography>
            <Typography sx={{ fontWeight: 300 }}>
              {momentDatetime.format("HH:mm")}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={10} md={10}>
            <Typography>{getTimelineItemTitle(item)}</Typography>
            <Typography>{item.eventDescription}</Typography>
            <Typography>
              {item.comment ? `Comment: ${item.comment}` : ""}
            </Typography>
          </Grid>
        </Grid>
      </Stack>
    );
  };

  const renderAmlDetails = () => {
    return (
      <Grid container>
        <Grid item xs={12} sm={12} md={12}>
          <Typography
            sx={{
              fontWeight: 600,
              color: investorAmlDetail && StatusColor[investorAmlDetail.status],
            }}
          >
            {investorAmlDetail && StatusNaming[investorAmlDetail.status]}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={4} md={4}>
          <Typography sx={{ fontStyle: "italic" }}>
            Account: {investorAmlDetail?.onboardingAccountId || ""}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={4} md={4}>
          <Typography sx={{ fontStyle: "italic" }}>
            Person: {investorAmlDetail?.personId || ""}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={4} md={4}>
          <Typography sx={{ fontStyle: "italic" }}>
            Customer: {investorAmlDetail?.customerName || ""}
          </Typography>
        </Grid>
      </Grid>
    );
  };

  return (
    <PageWrapper>
      <Stack justifyContent="space-between" direction="row">
        <Header title="AML Timeline Review" subtitle={""} />
        <Button
          variant="text"
          sx={{
            height: "32px",
            backgroundColor: "transparent",
            color: "primary.main",
            padding: "6px 8px",
            minWidth: "64px",
            fontSize: "0.875rem",
            fontWeight: 500,
            lineHeight: 1.75,
            borderRadius: "4px",
            textTransform: "uppercase",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "rgba(25, 118, 210, 0.04)",
              boxShadow: "none",
            },
          }}
          onClick={() => navigate(-1)}
        >
          Back
        </Button>
      </Stack>
      {id ? (
        <Stack sx={{ float: "right", marginBottom: "28px" }}>
          <AmlActions personId={id} />
        </Stack>
      ) : null}
      <Stack
        direction="column"
        rowGap="30px"
        sx={{
          border: "1px solid #f0f0f0",
          borderRadius: "8px",
          padding: "12px",
          backgroundColor: "#fff",
          width: "100%",
        }}
      >
        {renderAmlDetails()}
      </Stack>
      <Stack direction="column" rowGap="30px" sx={{ width: "100%" }}>
        <Timeline
          sx={{
            [`& .${timelineItemClasses.root}:before`]: {
              flex: 0,
              padding: 0,
            },
          }}
        >
          {timeLineData &&
            timeLineData.map((el, index) => {
              const isLast = timeLineData.length - 1 === index;
              return (
                <TimelineItem>
                  <TimelineSeparator>
                    <TimelineDot />
                    {isLast ? null : <TimelineConnector />}
                  </TimelineSeparator>
                  <TimelineContent>{renderTimelineContent(el)}</TimelineContent>
                </TimelineItem>
              );
            })}
        </Timeline>
      </Stack>
    </PageWrapper>
  );
};

function formatDateTime(
  dateString: string | number,
  timeString: string | number
) {
  dateString += "";
  timeString += "";
  const fixTimeStringLength = 6;
  const zeroPaddingTimeString = ("00" + timeString).slice(
    -fixTimeStringLength
  );
  const stamp = moment(
    dateString + zeroPaddingTimeString,
    "YYYYMMDDHHmmss"
  ).valueOf();
  return moment(stamp);
}

export default TimeLinePage;
